/**
 * Curated catalog of Koko's voice candidates. All from the ElevenLabs
 * Voice Library, picked for "excited Pokémon-companion kid" energy:
 * higher-pitched, animated, cheerful. The hard-coded `voiceSettings`
 * push every voice toward the more dynamic / emotional end of the
 * dial — lower stability + nudged style — since the default ElevenLabs
 * settings sound somewhat flat for a cartoon character.
 *
 * Voice IDs sourced from elevenlabs.io/voice-library:
 *   https://elevenlabs.io/voice-library/squeaky-voices
 *   https://elevenlabs.io/voice-library/playful
 */

export interface VoiceCandidate {
  /**
   * Stable key used in settings + the picker. Unique within our
   * catalog, even when two entries share an ElevenLabs voice ID
   * but differ in `playbackRate` (e.g. "alex-kid" vs "alex").
   */
  key: string;
  /** ElevenLabs voice ID — sent in the TTS request URL path. */
  voiceId: string;
  /** Short label shown in the picker UI. */
  label: string;
  /** One-line description shown next to the picker. */
  description: string;
  /** ElevenLabs voice_settings to send with each TTS request. */
  voiceSettings: ElevenLabsVoiceSettings;
  /**
   * Client-side playback-rate multiplier. ElevenLabs doesn't expose
   * any actual "child voice" model (their Voice Library bans uploads
   * that sound like minors), so we pitch + speed up the audio playback
   * via `audio.playbackRate` to push young-adult voices toward a
   * kid-companion register. 1.0 = unchanged. Values around 1.10–1.18
   * give a noticeable "excited kid" feel without going fully chipmunk.
   */
  playbackRate: number;
}

export interface ElevenLabsVoiceSettings {
  stability: number;
  similarity_boost: number;
  style: number;
  use_speaker_boost: boolean;
}

const EXCITED_KID_SETTINGS: ElevenLabsVoiceSettings = {
  // Lower stability gives a wider emotional range — needed for the
  // bouncy, varied delivery a kid character has. Default 0.5 sounds
  // monotone for cartoon work.
  stability: 0.35,
  similarity_boost: 0.75,
  // Push the natural style of the voice up a touch so excitable
  // voices stay excitable.
  style: 0.6,
  use_speaker_boost: true,
};

export const VOICE_CANDIDATES: readonly VoiceCandidate[] = [
  {
    key: "alex-kid",
    voiceId: "yl2ZDV1MzN4HbQJbMihG",
    label: "Alex (kid)",
    description: "Alex pitched up — excited little boy",
    voiceSettings: EXCITED_KID_SETTINGS,
    // 1.22 lifts Alex's clear young-adult tone firmly into kid range
    // without going full chipmunk. The slight tempo bump from
    // playbackRate doubles as added "excited kid" energy.
    playbackRate: 1.22,
  },
];

/** Single voice catalog entry — kept as an array so adding alternatives later is a one-line change. */
export const DEFAULT_VOICE_KEY = VOICE_CANDIDATES[0].key;

export function findVoiceCandidate(key: string): VoiceCandidate | undefined {
  return VOICE_CANDIDATES.find((candidate) => candidate.key === key);
}
